import { useMemo, useState } from "react";
import { TaxLayout } from "../../components/tax/TaxLayout.js";
import {
  Card,
  NumberField,
  Pill,
  SelectField,
  StatCard,
} from "../../components/design-system/index.js";
import { acaSubsidy } from "../../lib/util/aca.js";
import { usePreferences } from "../../lib/preferences.js";

type SunsetAssumption = "ira_extended" | "pre_arpa_baseline";

function formatCents(cents: number, hide: boolean): string {
  if (hide) return "••••";
  const sign = cents < 0 ? "-" : "";
  return `${sign}$${(Math.abs(cents) / 100).toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;
}

export function TaxAcaPlannerPage() {
  const { privacyMode } = usePreferences();
  const hide = privacyMode === true;

  const [magi, setMagi] = useState<number>(100_000_00);
  const [householdSize, setHouseholdSize] = useState<number>(4);
  const [assumption, setAssumption] = useState<SunsetAssumption>("ira_extended");

  const result = useMemo(
    () =>
      acaSubsidy({
        magi_cents: magi,
        household_size: householdSize,
        sunset_assumption: assumption,
      }),
    [magi, householdSize, assumption],
  );

  return (
    <TaxLayout
      title="ACA subsidy planner"
      subtitle="Modified Adjusted Gross Income (MAGI) vs Federal Poverty Level (FPL). Critical for early retirees managing the 400% FPL boundary."
    >
      <Card className="p-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
        <NumberField
          label="MAGI (cents)"
          value={magi}
          min={0}
          step={1_000_00}
          onChange={(e) => setMagi(Number(e.target.value))}
        />
        <NumberField
          label="Household size"
          value={householdSize}
          min={1}
          max={12}
          onChange={(e) => setHouseholdSize(Number(e.target.value))}
        />
        <SelectField
          label="Sunset assumption"
          value={assumption}
          onChange={(e) => setAssumption(e.target.value as SunsetAssumption)}
          helper="ARP+IRA extended through 2025 by default. 'Pre-ARPA' = current law for 2026 unless Congress acts."
        >
          <option value="ira_extended">IRA-extended through 2025</option>
          <option value="pre_arpa_baseline">Pre-ARPA baseline (cliff)</option>
        </SelectField>
      </Card>

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
        <StatCard
          label="MAGI as % FPL"
          value={`${result.your_fpl_pct.toFixed(1)}%`}
          sub={`${formatCents(result.your_magi_cents, hide)}`}
        />
        <StatCard
          label="100% FPL"
          value={formatCents(result.fpl_at_100_cents, hide)}
          sub={`Family of ${householdSize}`}
        />
        <StatCard
          label="400% FPL"
          value={formatCents(result.fpl_at_400_cents, hide)}
          sub="Cliff threshold"
        />
        <StatCard
          label="Distance to cliff"
          value={formatCents(result.distance_to_cliff_cents, hide)}
          sub={result.distance_to_cliff_cents < 0 ? "Above the cliff" : "Headroom remaining"}
        />
      </div>

      {result.above_cliff ? (
        <Card className="p-5 border border-[var(--color-danger)] bg-[var(--color-danger-soft)]">
          <div className="text-sm font-semibold text-[var(--color-danger)]">
            Above the 400% FPL cliff
          </div>
          <div className="text-sm mt-2 text-[var(--color-text)]">
            Under pre-ARPA rules (current law for 2026 unless Congress extends), you would receive{" "}
            <strong>$0 subsidy</strong>. The household pays the full SLCSP premium out-of-pocket.
          </div>
        </Card>
      ) : (
        <Card className="p-5">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-semibold text-[var(--color-text)]">
              Expected premium share
            </div>
            <Pill tone="accent">{result.applicable_pct.toFixed(2)}% of MAGI</Pill>
          </div>
          <div className="text-3xl font-semibold text-[var(--color-text)]">
            {formatCents(result.expected_premium_share_cents, hide)}
          </div>
          <div className="text-xs text-[var(--color-muted)] mt-1">
            What the household owes for the SLCSP at this MAGI. Subsidy = SLCSP_premium − this share.
          </div>
        </Card>
      )}

      <Card className="p-4 text-xs text-[var(--color-muted)]">
        <div className="mb-1">
          <strong>Sunset assumption</strong>: Hearth defaults to <em>IRA-extended through 2025</em>. Current law (pre-ARPA) reverts in plan year 2026 unless Congress extends. Toggle to see the cliff sensitivity — modeling both is critical for early-retiree planning.
        </div>
        <div>
          Source: IRC § 36B + ARPA § 9661 + IRA § 12001. FPL data: HHS 2025 Poverty Guidelines.
        </div>
      </Card>
    </TaxLayout>
  );
}
